import {
    AccountLicense,
    type Entitlement,
    Entitlements,
} from '@experiences/constants';
import {
    Features,
    useFeatureFlagValue,
} from '@experiences/feature-flags';
import {
    CheckAccessEvent,
    PortalTagManagementEvent,
    SecuritySettingsEvent,
    UserConsentEvent,
} from '@experiences/telemetry';
import { UiStack } from '@experiences/ui-common';
import { ApProgressSpinner } from '@uipath/portal-shell-react';
import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
    IPRestrictionLink,
    SessionPolicyLink,
} from '../../common/constants/documentation/DocumentationLinksDefault';
import {
    IPRestrictionLinkK8s,
    SessionPolicyLinkK8s,
} from '../../common/constants/documentation/DocumentationLinksK8s';
import { useDocumentationLinks } from '../../common/hooks/useDocumentationLink';
import useIsOnPrem from '../../common/hooks/useIsOnPrem';
import UpgradeForFeature from '../common/UpgradeForFeature';
import { useEntitlements } from '../entitlements/EntitlementsContextProvider';

export const UpgradeGuard: React.FC<{ children?: React.ReactNode; requiredEntitlement: Entitlement }> = ({
    children, requiredEntitlement,
}) => {
    const { formatMessage: translate } = useIntl();
    const getLocalizedLink = useDocumentationLinks();
    const EnablePricingV2 = useFeatureFlagValue(Features.EnablePricingV2.name);

    const isOnPrem = useIsOnPrem();

    const EntitlementToUpgradeMapRecord: Record<Entitlement, any> = useMemo(() => ({
        [Entitlements.IpRestrictions]: {
            upgradeTitle: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_IP_RESTRICTION_TITLE' : 'CLIENT_UPGRADE_IP_TITLE' }),
            upgradeMessage: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_IP_RESTRICTION_DESCRIPTION' : 'CLIENT_UPGRADE_IP_DESCRIPTION' }),
            documentationLink: getLocalizedLink({ articleSlug: isOnPrem ? IPRestrictionLinkK8s : IPRestrictionLink }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: SecuritySettingsEvent.IPRestriction,
        },
        [Entitlements.CustomerManagedKey]: undefined,
        [Entitlements.Insights]: undefined,
        [Entitlements.PlatformSapEnabled]: undefined,
        [Entitlements.SessionPolicy]: {
            upgradeTitle: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_SESSION_POLICY_TITLE' : 'CLIENT_UPGRADE_SESSION_TITLE' }),
            upgradeMessage: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_SESSION_POLICY_DESCRIPTION' : 'CLIENT_UPGRADE_SESSION_DESCRIPTION' }),
            documentationLink: getLocalizedLink({ articleSlug: isOnPrem ? SessionPolicyLinkK8s : SessionPolicyLink }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: SecuritySettingsEvent.SessionPolicy,
        },
        [Entitlements.AccessPolicy]: {
            upgradeTitle: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_ACCESS_POLICY_TITLE_REVAMP' : 'CLIENT_UPGRADE_ACCESS_POLICY_TITLE' }),
            upgradeMessage: translate({ id: EnablePricingV2 ? 'CLIENT_UPGRADE_ACCESS_POLICY_DESCRIPTION' : 'CLIENT_ONLY_ALLOWED_MEMBERS_DESCRIPTION' }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: SecuritySettingsEvent.AccessPolicy,
        },
        [Entitlements.CheckAccess]: {
            upgradeTitle: translate({ id: 'CLIENT_UPGRADE_CHECK_ACCESS_TITLE' }),
            upgradeMessage: translate({ id: 'CLIENT_UPGRADE_CHECK_ACCESS_DESCRIPTION' }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: CheckAccessEvent.CheckAccess,
        },
        [Entitlements.UserConsent]: {
            upgradeTitle: translate({ id: 'CLIENT_UPGRADE_USER_CONSENT_TITLE' }),
            upgradeMessage: translate({ id: 'CLIENT_UPGRADE_USER_CONSENT_DESCRIPTION' }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: UserConsentEvent.UserConsent,
        },
        [Entitlements.CustomMailSettings]: {
            upgradeTitle: translate({ id: 'CLIENT_UPGRADE_EMAIL_SETTINGS_TITLE' }),
            upgradeMessage: translate({ id: 'CLIENT_UPGRADE_EMAIL_SETTINGS_DESCRIPTION' }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: '',
        },
        [Entitlements.AiTrustLayerByoLlm]: undefined,
        [Entitlements.AiTrustLayerShowLlmOps]: undefined,
        [Entitlements.AiTrustLayerShowGovernance]: undefined,
        [Entitlements.SsoProvisioning]: undefined,
        [Entitlements.EligibleForMultiRegion]: undefined,
        [Entitlements.CanaryTenant]: undefined,
        [Entitlements.UIBranding]: undefined,
        [Entitlements.AppTester]: undefined,
        [Entitlements.AppTestDeveloper]: undefined,
        [Entitlements.EligibleForOrganizationExperience]: undefined,
        [Entitlements.EligibleForUserExperience]: undefined,
        [Entitlements.EligibleForResourceCatalogueTags]: {
            upgradeTitle: translate({ id: 'CLIENT_UPGRADE_RCS_TAGS_TITLE' }),
            upgradeMessage: translate({ id: 'CLIENT_UPGRADE_RCS_TAGS_DESCRIPTION' }),
            level: AccountLicense.ENTERPRISE,
            telemetryTitle: PortalTagManagementEvent.RcsTags,
        },
        [Entitlements.EligibleForCustomRoles]: undefined,
    }), [ translate, EnablePricingV2, getLocalizedLink, isOnPrem ]);

    const {
        hasEntitlement, isLoading: isLoadingEntitlements,
    } = useEntitlements();

    if (isLoadingEntitlements) {
        return <UiStack
            align="center"
            justify="center"
            grow={1}>
            <ApProgressSpinner />
        </UiStack>;
    }

    if (hasEntitlement(requiredEntitlement) || !EnablePricingV2) {
        return children;
    }

    return <UiStack
        align="center"
        justify="center"
        grow={1}>
        <UpgradeForFeature
            upgradeTitle={EntitlementToUpgradeMapRecord[requiredEntitlement]?.upgradeTitle ?? translate({ id: 'CLIENT_UPGRADE_FOR_FEATURE_TITLE_DEFAULT' })}
            upgradeMessage={EntitlementToUpgradeMapRecord[requiredEntitlement]?.upgradeMessage ?? translate({ id: 'CLIENT_UPGRADE_FOR_FEATURE_DESCRIPTION_DEFAULT' })}
            documentationLink={EntitlementToUpgradeMapRecord[requiredEntitlement]?.documentationLink ?? 'https://docs.uipath.com/'}
            level={EntitlementToUpgradeMapRecord[requiredEntitlement]?.level ?? AccountLicense.ENTERPRISE}
            telemetryTitle={EntitlementToUpgradeMapRecord[requiredEntitlement]?.telemetryTitle ?? 'UpgradeForFeature.UndefinedEvent'}
        />
    </UiStack>;
};
